/**
 * Async ports of the saga generators that constructed widget view-models
 * from `WidgetCreationInfo`. Used by `WorkspaceService.addWidget` (and the
 * thin saga shim that forwards into it) to assemble a fresh widget shape
 * without going through Redux saga effects.
 *
 * The original generator versions in `store/sagas/widgets/construction/`
 * are removed once the saga shim wraps these.
 */
import { Duration } from '@js-joda/core';
import type { QueryClient } from '@tanstack/query-core';
import type { WorkspaceConfig } from '@thinkalpha/platform-ws-client/contracts/workspace.js';
import type { WidgetCreationInfo, WidgetType } from '@thinkalpha/ui-types/contracts/workspace';
import { parseInstrument } from 'src/lib/instrumentUtils';
import { UnreachableCaseError } from 'src/lib/util/unreachableCaseError';
import { AlphaEyeWidgetModelImpl } from 'src/models/AlphaEyeWidgetModel/impl';
import { getWorkspaceConfigQuery } from 'src/queries/workspace';
import { defaultQuantityStep } from 'src/routes/widgets/LocatesWidget/formatting';
import { DEFAULT_INTERVAL, DEFAULT_TIMEZONE } from 'src/routes/widgets/TradingViewChartWidget/constants';
import type { RootState } from 'src/store';
import {
    createDefaultLocatesV2Widget,
    createDefaultScannerWidget,
    createDefaultScreenerWidget,
    createDefaultSearchAlphaWidget,
} from 'src/store/defaults/widget';
import type {
    AccountsWidgetViewModel,
    AlertsWidgetViewModel,
    BacktestWidgetViewModel,
    BasketTradingWidgetViewModel,
    EventsWidgetViewModel,
    ExecutionsWidgetViewModel,
    FilingsWidgetViewModel,
    LocatesV2WidgetViewModel,
    LocatesWidgetViewModel,
    MessagesWidgetViewModel,
    NewsWidgetViewModel,
    NotepadWidgetViewModel,
    OrderBlotterWidgetViewModel,
    OrdersWidgetViewModel,
    PlatformsWidgetViewModel,
    PositionsWidgetViewModel,
    ScannerWidgetViewModel,
    SearchAlphaWidgetViewModel,
    SymbolsWidgetViewModel,
    StrategyWidgetViewModel,
    TableWidgetViewModel,
    TradeProWidgetViewModel,
    TradersWidgetViewModel,
    TradingViewChartWidgetViewModel,
    WatchlistWidgetViewModel,
    WidgetTabViewModel,
    WidgetViewModel,
} from 'src/store/types';

export function getDefaultAlphaEyeWidget(
    defaultSymbol?: string,
    channelId: string | null = null,
): Extract<WidgetViewModel, { type: 'alpha-eye' }> {
    if (!defaultSymbol) {
        return {
            type: 'alpha-eye',
            channelId,
        };
    }

    const state = AlphaEyeWidgetModelImpl.stateSchema.decode({
        ...AlphaEyeWidgetModelImpl.stateSchema.encode(AlphaEyeWidgetModelImpl.defaultState()),
        symbol: defaultSymbol,
    });

    return {
        type: 'alpha-eye',
        channelId,
        ...state,
    };
}

export function getDefaultStrategyWidget(
    incomingIdeas: StrategyWidgetViewModel['ideas'],
    filter?: string,
    overrides?: Partial<Pick<StrategyWidgetViewModel, 'columnTemplate' | 'universeId' | 'scrolling' | 'columnOptions'>>,
): StrategyWidgetViewModel {
    return {
        type: 'results',
        channelId: null,
        ideas: incomingIdeas,
        // With no caller-supplied universe the creation-time instruction from
        // ResultsWidgetModelImpl.defaultInitialState() has the model resolve
        // the workspace default during its loading state, keeping the
        // workspace-config fetch off the widget-creation path so the widget
        // paints immediately.
        universeId: overrides?.universeId ?? null,
        columnTemplate: overrides?.columnTemplate ?? null,
        filter: filter || null,
        scrolling: overrides?.scrolling ?? false,
        columnOptions: overrides?.columnOptions ?? undefined,
    };
}

export function getDefaultWatchlistWidget(): WatchlistWidgetViewModel {
    return {
        type: 'watchlist',
        channelId: null,
        idea: {
            name: 'Unnamed Watchlist',
            key: 'ticker',
            description: 'Watchlist',
            plan: { type: 'watchlist', includeList: [] },
            defaultUniverseId: null,
            defaultColumnTemplateId: null,
            isTemplate: false,
        },
        universeId: null,
        columnTemplate: null,
        filter: null,
        defaultIdeaStatus: 'loading',
    };
}

export function getDefaultNewsWidget(): NewsWidgetViewModel {
    return {
        type: 'news',
        channelId: null,
        ideas: [],
        universeId: null,
        symbolFilter: null,
        symbolRetention: Duration.ofSeconds(0).toString(),
    };
}

export function getDefaultEventsWidget(): EventsWidgetViewModel {
    return {
        type: 'events',
        channelId: null,
        slowIdeaId: null,
        fastIdeaIds: [],
        universeId: null,
        symbolFilter: '',
        gotoDate: null,
        symbolRetention: Duration.ofSeconds(0).toString(),
        displayingCalendar: false,
        splitPercentage: 50,
    };
}

export function getDefaultFilingsWidget(): FilingsWidgetViewModel {
    return {
        type: 'filings',
        channelId: null,
        universeId: null,
        symbolFilter: null,
        filingTypes: ['8-K'],
        gotoDate: null,
        symbolRetention: Duration.ofSeconds(0).toString(),
    };
}

/**
 * Async port of the previous saga generator. Constructs a `WidgetViewModel`
 * from a `WidgetCreationInfo`, using the supplied store state for selectors
 * (cloned widgets) and the query client for any fetches (default-symbol
 * lookups, workspace config). Pure with respect to provided arguments —
 * no Redux dispatches, no IoC context.
 */
export async function getWidgetFromCreationInfo(
    creationInfo: WidgetCreationInfo,
    state: RootState,
    queryClient: QueryClient,
): Promise<WidgetViewModel> {
    const channelId = 'channelId' in creationInfo ? creationInfo.channelId : null;

    switch (creationInfo.creationType) {
        case 'cloned': {
            const widgetTab: WidgetTabViewModel | undefined = state.tab.byId[creationInfo.baseWidgetTabId];
            if (!widgetTab) {
                throw new Error(
                    `getWidgetFromCreationInfo: cloned base tab '${creationInfo.baseWidgetTabId}' not in state`,
                );
            }
            return widgetTab.widget;
        }

        case 'new-alpha-eye':
        case 'new-alpha-lens': {
            return getDefaultAlphaEyeWidget(creationInfo.defaultSymbol, creationInfo.channelId);
        }

        case 'new-tradingview-chart': {
            const widget: TradingViewChartWidgetViewModel = {
                type: 'tradingview-chart',
                channelId,
                symbol: creationInfo.defaultSymbol,
                interval: creationInfo.interval ?? DEFAULT_INTERVAL,
                timezone: DEFAULT_TIMEZONE,
            };
            return widget;
        }

        case 'new-searchalpha': {
            const workspaceConfig: WorkspaceConfig = await queryClient.fetchQuery(getWorkspaceConfigQuery());
            const widget: SearchAlphaWidgetViewModel = createDefaultSearchAlphaWidget({
                universeId: workspaceConfig.defaultUniverseId,
                idea: creationInfo.idea,
                channelId: creationInfo.channelId,
            });
            return widget;
        }

        case 'new-strategy': {
            return getDefaultStrategyWidget(
                creationInfo.strategies,
                creationInfo.filter ?? creationInfo.defaultSymbol,
                {
                    columnTemplate: creationInfo.columnTemplate,
                    universeId: creationInfo.universeId,
                    scrolling: creationInfo.scrolling,
                    columnOptions: creationInfo.columnOptions,
                },
            );
        }

        case 'new-news': {
            const base = getDefaultNewsWidget();
            const symbolFilter =
                'symbolFilter' in creationInfo ? creationInfo.symbolFilter : creationInfo.defaultSymbol;
            return { ...base, channelId: creationInfo.channelId, symbolFilter };
        }

        case 'new-events': {
            const base = getDefaultEventsWidget();
            return {
                ...base,
                channelId: creationInfo.channelId,
                symbolFilter: creationInfo.symbolFilter,
                ...(creationInfo.eventTypes ? { eventTypes: creationInfo.eventTypes } : {}),
            };
        }

        case 'from-widget-default': {
            return getDefaultFromWidgetType(creationInfo.widgetType, channelId, queryClient);
        }

        default:
            throw new UnreachableCaseError(creationInfo);
    }
}

async function getDefaultFromWidgetType(
    widgetType: WidgetType,
    channelId: string | null,
    queryClient: QueryClient,
): Promise<WidgetViewModel> {
    switch (widgetType) {
        case 'alpha-eye':
        case 'alpha-lens':
            return getDefaultAlphaEyeWidget(undefined, channelId);
        case 'results':
            return getDefaultStrategyWidget([]);
        case 'watchlist':
            return getDefaultWatchlistWidget();
        case 'news':
            return getDefaultNewsWidget();
        case 'events':
            return getDefaultEventsWidget();
        case 'filings':
            return getDefaultFilingsWidget();
        case 'scanner': {
            const widget: ScannerWidgetViewModel = createDefaultScannerWidget({ defaultIdeaStatus: 'loading' });
            return widget;
        }
        case 'screener':
            return createDefaultScreenerWidget();
        case 'searchalpha': {
            const workspaceConfig: WorkspaceConfig = await queryClient.fetchQuery(getWorkspaceConfigQuery());
            const widget: SearchAlphaWidgetViewModel = createDefaultSearchAlphaWidget({
                universeId: workspaceConfig.defaultUniverseId,
            });
            return widget;
        }
        case 'order-blotter': {
            const widget: OrderBlotterWidgetViewModel = {
                type: 'order-blotter',
                channelId,
                accountIds: null,
                statusFilter: null,
                viewData: null,
                formattingSettings: null,
                tickerFilter: null,
                instrument: parseInstrument(''),
            };
            return widget;
        }
        case 'accounts': {
            const widget: AccountsWidgetViewModel = {
                type: 'accounts',
                channelId,
                columnTemplate: null,
                platforms: null,
                accountIds: null,
                userIds: null,
                filter: null,
            };
            return widget;
        }
        case 'notepad': {
            const widget: NotepadWidgetViewModel = {
                type: 'notepad',
                channelId: null,
                content: '',
            };
            return widget;
        }
        case 'traders': {
            const widget: TradersWidgetViewModel = {
                type: 'traders',
                channelId: null,
            };
            return widget;
        }
        case 'platforms': {
            const widget: PlatformsWidgetViewModel = {
                type: 'platforms',
                channelId: null,
            };
            return widget;
        }
        case 'symbols': {
            const widget: SymbolsWidgetViewModel = {
                type: 'symbols',
                channelId: null,
            };
            return widget;
        }
        case 'alerts': {
            const widget: AlertsWidgetViewModel = {
                type: 'alerts',
                channelId: null,
                activeAlertsTab: 'symbols',
                linkedSymbol: null,
            };
            return widget;
        }
        case 'tradingview-chart': {
            const widget: TradingViewChartWidgetViewModel = {
                type: 'tradingview-chart',
                channelId,
                symbol: 'SPY',
                interval: DEFAULT_INTERVAL,
                timezone: DEFAULT_TIMEZONE,
            };
            return widget;
        }
        case 'trade-pro':
        case 'trade-pro-multi': {
            const type =
                widgetType === 'trade-pro'
                    ? 'trade-pro'
                    : widgetType === 'trade-pro-multi'
                      ? 'trade-pro-multi'
                      : (() => {
                            throw new UnreachableCaseError(widgetType);
                        })();
            const widget: TradeProWidgetViewModel = {
                type,
                channelId,
                accountId: null,
                skipTradeConfirmation: false,
                symbol: null,
                instrument: parseInstrument(''),
            };
            return widget;
        }
        case 'basket-trading': {
            const widget: BasketTradingWidgetViewModel = {
                type: 'basket-trading',
                channelId,
            };
            return widget;
        }
        case 'messages': {
            const widget: MessagesWidgetViewModel = {
                type: 'messages',
                channelId,
                accountIds: null,
                sourceFilter: null,
                statusFilter: null,
                viewData: null,
                formattingSettings: null,
                instrument: parseInstrument(''),
            };
            return widget;
        }
        case 'positions': {
            const widget: PositionsWidgetViewModel = {
                type: 'positions',
                channelId,
                columnTemplate: null,
                platforms: null,
                accountIds: null,
                userIds: null,
                filter: null,
                instrument: parseInstrument(''),
            };
            return widget;
        }
        case 'orders':
        case 'manual-review': {
            const type =
                widgetType === 'orders'
                    ? 'orders'
                    : widgetType === 'manual-review'
                      ? 'manual-review'
                      : (() => {
                            throw new UnreachableCaseError(widgetType);
                        })();
            const widget: OrdersWidgetViewModel = {
                type,
                channelId,
                columnTemplate: null,
                platforms: null,
                accountIds: null,
                userIds: null,
                filter: null,
                instrument: parseInstrument(''),
                isManualReviewSoundAlertEnabled: true,
            };
            return widget;
        }
        case 'table': {
            const widget: TableWidgetViewModel = {
                type: 'table',
                channelId,
                table: null,
                class: null,
                filter: null,
                viewData: null,
            };
            return widget;
        }
        case 'executions': {
            const widget: ExecutionsWidgetViewModel = {
                type: 'executions',
                channelId,
                columnTemplate: null,
                platforms: null,
                accountIds: null,
                userIds: null,
                filter: null,
                instrument: parseInstrument(''),
            };
            return widget;
        }
        case 'locates': {
            const widget: LocatesWidgetViewModel = {
                type: 'locates',
                channelId,
                accountIds: null,
                filter: null,
                isQuantityKilo: false,
                isSoundAlertEnabled: false,
                selectedTab: 'all',
                isUserOnly: true,
                formData: {
                    accountId: null,
                    symbol: '',
                    quantity: defaultQuantityStep,
                    providerId: null,
                },
                ordersTableData: {
                    viewData: null,
                    formattingSettings: null,
                },
                costInfoTableData: {
                    columnTemplate: null,
                },
                quickSendButtons: [],
            };
            return widget;
        }
        case 'lab': {
            const widget: LocatesV2WidgetViewModel = createDefaultLocatesV2Widget({
                channelId,
                accountIds: null,
                isQuantityKilo: false,
                isSoundAlertEnabled: false,
                isUserOnly: true,
                formData: {
                    accountId: null,
                    symbol: '',
                    quantity: defaultQuantityStep,
                    providerId: null,
                },
                quickSendButtons: [],
            });
            return widget;
        }
        case 'backtest': {
            const widget: BacktestWidgetViewModel = {
                type: 'backtest',
                channelId,
            };
            return widget;
        }
        default:
            throw new UnreachableCaseError(widgetType);
    }
}
